import { EventBus } from '../EventBus.js';
import identidadSesion, { sigueLaSesion } from './identidadSesion.js';
import preloaderDeLaSesion from './preloaderDeLaSesion.js';
import {
  alDejarDeMandar, alDejarDeMandarLaPantalla, selloDeLaPantalla, selloDeLaVentana
} from './soloMandaLaDeDelante.js';
import { marcarEnVuelo, cerrojoPuesto, SIN_IDENTIFICAR } from './unaAccionALaVez.js';
import { comoPromesaComun, esCancelacion } from './sesionDePeticiones.js';

/**
 * **Una acción lleva dentro la sesión con la que se empezó.**
 *
 * `identidadSesion` dice de qué sesión es un dato; esto dice de qué sesión es una ACCIÓN, que es
 * otra pregunta y se contesta antes. Entre que alguien pulsa «Aprobar» y que la acción sale hacia
 * el servidor hay un diálogo esperando a una persona: una confirmación, o un `prompt` en el que
 * hay que escribir el motivo. Ese rato no tiene tope —se puede dejar abierto y volver mañana—, y
 * en medio cabe cerrar sesión y entrar con otra cuenta en la misma pestaña.
 *
 * Si eso pasa, al aceptar la petición se creaba **con el token de la cuenta nueva**: una segunda
 * cuenta resolvía la solicitud que había elegido la primera, y todos los efectos posteriores
 * —`rh_audit` incluido— se le atribuían a ella (revisión CODEX del PR #1735, ronda 4). Y la
 * comprobación que ya había *después* del POST no lo descartaba, porque tomaba la identidad al
 * enviar: para entonces la cuenta nueva ya era «la de ahora» y todo cuadraba.
 *
 * La regla, que es **una** y la fijó dirección el 07/10/2026 en la #59:
 *
 * > Un diálogo lleva dentro la identidad con la que se abrió. Al aceptar, si la sesión que hay
 * > ahora no es ésa, no se ejecuta: se cierra y se dice por qué. Nunca se envía la acción con una
 * > sesión distinta de la que la empezó.
 *
 * ## Por qué vive aquí y no en cada pantalla
 *
 * Porque son cinco diálogos —aprobar y denegar en la bandeja, los mismos dos en el calendario, y
 * anular en «Mis vacaciones»— y la ronda anterior ya tapó los dos primeros por separado, cada uno
 * con su guarda, dejando los otros tres. Una guarda repartida por llamante se olvida en el
 * siguiente que se escriba; aquí el sello se toma al construir el ayudante y **no hay forma de
 * abrir el diálogo sin él**. Es la misma decisión que se tomó con el sello de sesión del carril
 * (`carrilPeticiones`, #262): un solo vigilante, sin junturas donde equivocarse.
 *
 * ## Las dos mitades de la misma identidad
 *
 * `confirmar`/`pedir` cierran el hueco de ANTES del envío, y `enviar` el de DESPUÉS —el acuse, el
 * diálogo del error, la recarga y el aviso al badge, que son efectos de fuera del componente y se
 * ejecutaban igual sobre la pantalla de quien acababa de entrar—. Las dos comparan contra **el
 * mismo sello**, el de cuando se abrió el diálogo, que es lo que une las dos mitades: una acción
 * empezada por ANA no se envía con la sesión de BENITO, y si se envió con la de ANA, su resultado
 * tampoco se le cuenta a BENITO.
 *
 * Y lo mismo con la pantalla y la ventana de abajo: al aceptar se preguntan las dos, no solo la
 * sesión. Un diálogo abierto en «Anular días» de ANA no se acepta sobre la ventana reabierta para
 * BENITO (ver `selladoCon`).
 *
 * ## Y las otras dos preguntas que una acción en vuelo se tiene que hacer
 *
 * La sesión no es la única que cambia mientras el POST viaja, y las tres respuestas viven aquí por
 * la misma razón por la que vive la primera —son los mismos cuatro diálogos y los que vengan—:
 *
 *  - **«¿sigue delante esta pantalla?»** (`selloDeLaPantalla`). Las rutas del módulo son
 *    `keepAlive`: salir de ellas no destruye nada y la sesión es la misma, así que el sello de
 *    arriba decía que sí y la recarga del calendario se lanzaba igual desde una pantalla que ya
 *    nadie mira, con el preloader global encendido sobre la de destino. Lo que se descarta es lo
 *    que es **de la pantalla** —recargar la rejilla, cerrar el detalle—; lo que es de fuera, como
 *    el badge del menú lateral, va en `fuera` y se hace igual: la resolución ocurrió, y el número
 *    del menú es del menú;
 *  - **«¿sigue siendo de esta solicitud la ventana que la lanzó?»** (`selloDeLaVentana`, #69). Una
 *    ventana del módulo es una sola instancia que la pantalla reutiliza: cerrarla y abrirla sobre
 *    otra persona no es ninguna de las salidas de la pantalla, así que el acuse de la anulación de
 *    ANA se enseñaba con el nombre de BENITO y el aviso de «ya está» le cerraba su formulario
 *    (revisión CODEX del PR #1748, ronda 9). Lo que responde a esta pregunta es lo que es **de la
 *    ventana**: su acuse, su diálogo de error y su propio cierre;
 *  - **«¿hay ya una acción en vuelo sobre esto?»** ({@link marcarEnVuelo}). El diálogo se cierra al
 *    aceptar, no al contestar el servidor, así que los botones quedaban pulsables durante el viaje
 *    y se podía mandar la misma resolución dos veces —o la contraria— con la misma `version`. Ese
 *    cerrojo es de **la solicitud** y vive en el módulo, no en la pantalla: ni desmontarla ni
 *    volver a montarla lo mueven, y el cambio de cuenta lo suelta (ver `unaAccionALaVez`). Aquí
 *    está la guarda que lo usa: **no se abre ni se acepta un diálogo sobre algo que ya se está
 *    resolviendo**, y se dice. Apagar el botón es la mitad visible; la garantía es ésta, y por eso
 *    no depende de que una plantilla se acuerde de atar el `:disabled`.
 *
 * Todas salieron de la misma forma: algo cambia mientras la petición viaja y nadie lo mira al
 * volver. Y por eso los efectos van **repartidos por a quién pertenecen** y no todos juntos: la
 * ventana (`exito`, `fallo`), la pantalla (`pantalla`) y lo de fuera (`fuera`).
 *
 * ## La pregunta es de la acción, de que se abre a que se cierra (#271)
 *
 * El sello decide qué pasa **al aceptar**; lo que no decidía nadie es la vida de la pregunta en sí.
 * `confirmar` y `pedir` abrían el diálogo y se olvidaban de él, y la tarjeta del interruptor —que
 * necesita sus propios botones— lo montaba por su cuenta y guardaba **uno** para cerrarlo al salir.
 * Con dos pulsaciones antes de que el diálogo tapara el botón se abrían dos preguntas, se cerraba
 * una sola y la otra se quedaba encima de la pantalla de destino (revisión CODEX del PR #1867,
 * ronda 5). Era la cuarta vez de la misma familia en esa tarjeta, y la decisión técnica de
 * Donchurru del 23/10/2026 fue cerrarla aquí, para todas las acciones, con este contrato:
 *
 *  - **se reserva antes de abrir**: mientras haya una pregunta de una acción en pantalla, no se
 *    abre otra —ni de esta acción ni de ninguna—, y la segunda pulsación no hace nada. Los diálogos
 *    son modales: si ya hay uno, quien pulsa lo tiene delante;
 *  - **se acepta una sola vez**, antes de construir la petición: el segundo «Sí» —o el de un botón
 *    que llega tarde— no llega al callback;
 *  - **se cancela sola** al salir de la pantalla o de la ventana, al destruirse quien la abrió y al
 *    cambiar de sesión, y se cierra el diálogo. Lo que se cancela no dice nada: quien lo abrió ya
 *    no está delante, y un aviso suyo saldría sobre la pantalla de otra persona o de otra cosa;
 *  - **cancelar, el Escape, el Atrás y un fallo al abrir la sueltan** igual, y cada una suelta solo
 *    **su** reserva: el cierre tardío de una pregunta vieja no libera la de una acción posterior.
 *
 * Por eso todas las preguntas pasan por aquí —{@link confirmar}, {@link pedir} y {@link abrir}
 * para los diálogos con botones propios— y ninguna pantalla guarda el diálogo para cerrarlo ella.
 * Lo que ya salió hacia el servidor no se retira: su respuesta la descarta `enviar` con los sellos.
 *
 * El `preloader` sí se apaga siempre: es global y dejarlo encendido bloquea la pantalla de quien
 * acaba de entrar —o la de destino, si solo se ha navegado—. Y «siempre» incluye que la petición
 * **no termine nunca**, que es lo que el `then` de abajo no podía garantizar: lo resuelve
 * `preloaderDeLaSesion`, que lo cierra en cuanto la sesión se va o se sale de la pantalla.
 *
 *   const accion = accionDeLaSesion(self, claveDeSolicitud(solicitud.id));
 *   accion.confirmar(texto, titulo, function() {
 *     accion.enviar(RhBandejaVacaciones.aprobar(id, version, null), {
 *       exito: function() { ... },    // de la ventana: el acuse y cerrarla
 *       fallo: function(e) { ... },   // también de la ventana: su diálogo de error
 *       pantalla: function() { ... }, // de la pantalla: recargar la rejilla
 *       fuera: function() { ... }     // de fuera: el badge del menú lateral
 *     });
 *   });
 *
 * Una pantalla que no sea una ventana —la bandeja, el calendario— no necesita distinguir las dos
 * primeras: para ella `exito` y `pantalla` responden a la misma pregunta, porque su sello de
 * ventana ES el de la pantalla.
 *
 * El componente que las lanza usa además el mixin `unaAccionALaVez` y apaga con
 * `accionEnVueloSobre(...)` los controles que llevan aquí.
 *
 * @param vm el componente que abre el diálogo: de él salen `$f7` y el texto del aviso
 * @param sobre **sobre qué es la acción** —la clave de la solicitud que se va a resolver—, que es
 *        de lo que se coge el cerrojo. Sin ella se coge el genérico: una acción sin identificar
 *        apaga de más, que es lo que hay que hacer cuando no se sabe de qué es
 * @param opciones `sigueAbierto`, para la acción que se empieza desde un formulario que **no** es
 *        una ventana con sello —el popup «Crear planificación» del calendario—: dice si ese
 *        formulario, el mismo, sigue abierto, y `avisoCerrado` es lo que se dice si se acepta con él
 *        cerrado. Se pregunta junto a la sesión, la pantalla y la ventana **antes de enviar**; lo que
 *        vuelve del POST no depende de él, porque la acción ya salió y su error hay que contarlo
 *        aunque se haya cerrado el formulario mientras viajaba (#268, revisión CODEX del PR #1842)
 */

/** En qué punto está una pregunta. Solo se sale de `ABIERTA`, y una sola vez. */
const ABIERTA = 'abierta';
const ACEPTADA = 'aceptada';
const CERRADA = 'cerrada';

/**
 * **La pregunta de una acción que hay ahora mismo en pantalla**, o `null`. Del módulo y no de cada
 * acción: lo que se reserva es la pantalla, no la solicitud, y dos acciones distintas pulsadas a la
 * vez también abrirían dos diálogos uno detrás de otro (Framework7 los encola).
 */
let preguntaEnPantalla = null;

/**
 * El cambio de sesión cancela la que haya, sin esperar a ninguna pantalla: el diálogo es de la
 * aplicación y queda por encima del login. Los dos avisos, como el cerrojo y la espera: `close` es
 * salir y `open` que ha entrado otra cuenta.
 */
function cancelarLaPreguntaEnPantalla() {
  if (preguntaEnPantalla) preguntaEnPantalla.cancelar();
}
EventBus.$on('app:session:close', cancelarLaPreguntaEnPantalla);
EventBus.$on('app:session:open', cancelarLaPreguntaEnPantalla);

export default function accionDeLaSesion(vm, sobre, opciones) {
  const app = vm.$f7;
  const sigueAbierto = (opciones && opciones.sigueAbierto) || function() { return true; };
  // Sobre qué es. Se avisa por consola y se sigue con el cerrojo genérico: quedarse sin cerrojo
  // sería volver a dejar mandar la misma resolución dos veces, y reventar aquí dejaría la acción
  // sin hacer por un aviso que es para quien escribe la pantalla, no para quien la usa.
  if (!sobre) {
    console.error('[RH-VAC] `accionDeLaSesion` sin decir sobre qué es la acción: su cerrojo apaga '
      + 'entonces todas las acciones del módulo, no solo las de esa solicitud. Pásale '
      + '`claveDeSolicitud(id)`.');
  }
  const clave = sobre || SIN_IDENTIFICAR;
  // El sello se toma AQUÍ, al construir el ayudante, que es justo antes de dibujar el diálogo. Es
  // el único momento en el que la identidad es con seguridad la de quien está pulsando.
  const deLaSesion = identidadSesion();
  // Y de qué PANTALLA es, que es la otra mitad y se toma en el mismo momento y por el mismo motivo
  // (ver `soloMandaLaDeDelante`).
  const sigueDelante = selloDeLaPantalla(vm);
  // Y de qué VENTANA, que es la tercera: la pantalla puede seguir delante y la ventana haberse
  // reabierto sobre otra persona (#69).
  const sigueLaVentana = selloDeLaVentana(vm);
  // Y los textos, también ahora: cuando hagan falta, el componente puede estar ya destruido.
  const aviso = vm.$t('common.sesionCambiada');
  const avisoOtraVentana = vm.$t('common.ventanaCambiada');
  const avisoEnVuelo = vm.$t('common.accionEnVuelo');
  const avisoCerrado = opciones && opciones.avisoCerrado ? vm.$t(opciones.avisoCerrado) : avisoOtraVentana;
  // El preloader del envío. Se construye con la acción —no al enviar— para que sea el mismo si
  // alguien envía dos veces, y para que cerrarlo sea idempotente: el `then` de abajo y el cierre
  // por cambio de sesión pueden llegar los dos.
  const preloader = preloaderDeLaSesion(vm);
  // El cerrojo que la acción coge al EMPEZAR (`empezar`), o `null`: el que suelta `enviar`, o
  // `terminar` si la acción se abandona antes de salir. Y si ya ha salido, para que `terminar` —un
  // diálogo que se cierra después de aceptar, por ejemplo— no suelte lo que ya es del envío.
  let propio = null;
  let enviada = false;
  // La baja del apunte de la salida de pantalla que pone `empezar`, o `null`. Se quita en cuanto
  // la acción sale o se abandona, para no engordar la lista de la pantalla con cada alta.
  let dejarDeApuntarLaSalida = null;
  // La pregunta de ESTA acción, o `null` si todavía no ha preguntado. Una acción pregunta una vez:
  // cada pulsación construye la suya (ver `preguntar`).
  let pregunta = null;

  function noApuntarLaSalida() {
    if (!dejarDeApuntarLaSalida) return;
    dejarDeApuntarLaSalida();
    dejarDeApuntarLaSalida = null;
  }

  function terminar() {
    // Abandonar la acción con la pregunta todavía abierta la cancela, y con ella se va el diálogo:
    // cerrar el formulario de alta del calendario con su aviso delante, por ejemplo.
    if (pregunta && pregunta.estado === ABIERTA) pregunta.cancelar();
    noApuntarLaSalida();
    if (enviada) return;
    if (propio) {
      propio();
      propio = null;
    }
    preloader.cerrar();
  }

  /**
   * **La guarda del cerrojo, y está aquí porque aquí pasan todas.** Si esa solicitud ya se está
   * resolviendo, no se abre el diálogo y, si ya estaba abierto, aceptarlo no manda nada. Y **lo
   * dice**: callada, la acción se queda sin hacer en silencio y quien la pidió cree que sí se hizo.
   *
   * Hacen falta las dos mitades, y son dos momentos distintos: el toque que llega en el mismo
   * instante en que el botón se apaga (abrir) y el diálogo que llevaba un rato abierto cuando otro
   * ya resolvió lo mismo (aceptar). Repartido por llamante era un `if` copiado en cinco sitios, y
   * el quinto se olvida.
   */
  function yaSeEstaResolviendo() {
    if (!cerrojoPuesto(clave)) return false;
    app.dialog.alert(avisoEnVuelo);
    return true;
  }

  /**
   * El callback de aceptar, sellado. No ejecuta nada con otra sesión, desde una pantalla o una
   * ventana que ya no son las que abrieron el diálogo, ni con la acción ya en vuelo, y en los tres
   * casos lo dice.
   *
   * **La pantalla y la ventana se preguntan aquí también, y no solo al volver el POST.** Con solo
   * la sesión, cerrar «Anular días» de ANA con el «¿Seguro?» abierto y reabrirla sobre BENITO
   * dejaba aceptar un diálogo que hablaba de ANA y que creaba la petición leyendo la ventana de
   * BENITO: su solicitud, su tipo y su motivo, con los días de ANA (revisión CODEX del PR #1748,
   * ronda 2 del 20/10). Descartar los efectos a la vuelta no lo arreglaba, porque para entonces la
   * anulación ya estaba hecha. Es la misma regla de la #59 con las otras dos preguntas: el diálogo
   * se aceptó sobre algo que ya no está delante, así que no se envía.
   */
  function selladoCon(aceptar) {
    return function() {
      if (!sigueLaSesion(deLaSesion)) {
        terminar();
        app.dialog.alert(aviso);
        return undefined;
      }
      if (!sigueDelante() || !sigueLaVentana()) {
        terminar();
        app.dialog.alert(avisoOtraVentana);
        return undefined;
      }
      if (!sigueAbierto()) {
        terminar();
        app.dialog.alert(avisoCerrado);
        return undefined;
      }
      // El cerrojo que esta misma acción cogió al empezar no la frena a ella: es el que frena a
      // las demás.
      if (!propio && yaSeEstaResolviendo()) return undefined;
      return aceptar.apply(null, arguments);
    };
  }

  /** Si lo que empezó esto sigue delante: la sesión, la pantalla, la ventana y el formulario. */
  function sigueTodoDelante() {
    return sigueLaSesion(deLaSesion) && sigueDelante() && sigueLaVentana() && sigueAbierto();
  }

  /** Quita los apuntes de una pregunta y, si es la que hay en pantalla, deja libre el sitio. */
  function soltarLaPregunta(esta) {
    esta.bajas.splice(0).forEach(function(baja) { baja(); });
    if (preguntaEnPantalla === esta) preguntaEnPantalla = null;
  }

  /**
   * Una pregunta que se acaba sin que nadie la acepte: «Cancelar», Escape, el Atrás que cierra el
   * diálogo, un fallo al abrirlo, o cualquiera de las salidas. Suelta su reserva —la suya y no la
   * que haya ahora— y abandona la acción. Idempotente: el cierre del diálogo que provoca ella misma
   * vuelve a llegar aquí.
   */
  function abandonar(esta, cerrarElDialogo) {
    if (esta.estado !== ABIERTA) return;
    esta.estado = CERRADA;
    soltarLaPregunta(esta);
    if (cerrarElDialogo && esta.dialogo && typeof esta.dialogo.close === 'function') {
      try {
        esta.dialogo.close();
      } catch (e) {
        // Un diálogo que no se deja cerrar no puede dejar la acción sin abandonar.
        console.error('[RH-VAC] No se ha podido cerrar la pregunta de una acción cancelada', e);
      }
    }
    terminar();
  }

  /**
   * **El botón que acepta, una sola vez.** El primero que llega se queda con la pregunta y suelta
   * la reserva —ya está contestada— antes de pasar por el sello; los demás, y cualquiera que llegue
   * con la pregunta ya cancelada, no hacen nada y no dicen nada. Es lo que impide que dos «Sí» —o el
   * de un diálogo que se quedó encolado— construyan dos peticiones.
   *
   * Si lo aceptado no envía nada —un motivo vacío, ir a otra pantalla—, la acción se abandona aquí:
   * lo que hubiera cogido con `empezar` no se queda puesto esperando un envío que no va a llegar.
   */
  function aceptarUnaVez(esta, aceptar) {
    const sellado = selladoCon(aceptar);
    return function() {
      if (esta.estado !== ABIERTA) return undefined;
      esta.estado = ACEPTADA;
      soltarLaPregunta(esta);
      try {
        return sellado.apply(null, arguments);
      } finally {
        if (!enviada) terminar();
      }
    };
  }

  /**
   * **Todas las preguntas pasan por aquí**, sea cual sea el diálogo (ver «La pregunta es de la
   * acción» arriba). `construir` recibe con qué sellar cada botón que acepta y con qué cancelar, y
   * devuelve la instancia de Framework7; `abrirDespues`, para la que se crea sin abrir
   * (`dialog.create`), que se abre cuando ya se está escuchando su cierre.
   *
   * No se pregunta, y la acción se abandona, si esta acción ya preguntó, si lo que la empezó ya no
   * está delante —un callback que llega tarde no abre nada sobre la pantalla siguiente—, si ya hay
   * una acción en vuelo sobre lo mismo (y entonces se dice) o si ya hay otra pregunta en pantalla.
   *
   * @return la instancia del diálogo, o `undefined` si no se ha preguntado
   */
  function preguntar(construir, abrirDespues) {
    if (pregunta) return undefined;
    if (!sigueTodoDelante() || preguntaEnPantalla || (!propio && yaSeEstaResolviendo())) {
      terminar();
      return undefined;
    }
    const esta = { estado: ABIERTA, dialogo: null, bajas: [] };
    esta.cancelar = function() { abandonar(esta, true); };
    pregunta = esta;
    preguntaEnPantalla = esta;
    // Las salidas, apuntadas en los dos sellos: el de la ventana y el de la pantalla (ver
    // `alDejarDeMandarLaPantalla`). Y la destrucción de quien la abre, que puede no ser ninguno de
    // los dos: la bandeja se desmonta dentro de «Vacaciones» sin que la pantalla se vaya.
    esta.bajas.push(alDejarDeMandar(vm, esta.cancelar));
    esta.bajas.push(alDejarDeMandarLaPantalla(vm, esta.cancelar));
    if (typeof vm.$once === 'function') {
      vm.$once('hook:beforeDestroy', esta.cancelar);
      esta.bajas.push(function() { vm.$off('hook:beforeDestroy', esta.cancelar); });
    }
    try {
      esta.dialogo = construir(function(aceptar) { return aceptarUnaVez(esta, aceptar); },
        function() { abandonar(esta, false); });
      // `close` y no `closed`: llega en el acto, por cualquier causa —también el Atrás de Android,
      // que cierra el diálogo sin pasar por ningún botón—, y después del `onClick` del botón que lo
      // cierra (Framework7 llama al botón y luego cierra), así que lo aceptado ya no está abierto.
      if (esta.dialogo && typeof esta.dialogo.on === 'function') {
        esta.dialogo.on('close', function() { abandonar(esta, false); });
      }
      if (abrirDespues && esta.estado === ABIERTA && esta.dialogo) esta.dialogo.open();
    } catch (e) {
      abandonar(esta, true);
      throw e;
    }
    return esta.dialogo;
  }

  return {
    /**
     * **El sello a secas**, para la continuación de algo que no es una pregunta —el final de una
     * consulta previa, que también deja un hueco entre pulsar y enviar—. Es el mismo `selladoCon`
     * de las preguntas, así que la regla de la #59 sigue viviendo en un solo sitio: salió con el
     * «Crear planificación» del calendario, que enviaba el borrador de la cuenta anterior con el
     * token de la nueva (revisión CODEX del PR #1842, ronda 3).
     *
     * Un botón de diálogo **no** se sella con esto: va por {@link abrir}, que además de sellarlo
     * es dueño de la pregunta (#271).
     */
    sellar: function(aceptar) {
      return selladoCon(aceptar);
    },
    /**
     * **Una acción de varios pasos es una sola desde el primer clic.** Entre pulsar y enviar, el
     * «Crear planificación» del calendario hace dos consultas y, si es una vacación, espera a que
     * una persona conteste un aviso. Con el cerrojo cogido solo en `enviar`, un segundo clic en ese
     * hueco abría su propio aviso y mandaba otra alta igual (revisión CODEX del PR #1842, ronda 4).
     *
     * `empezar` coge el cerrojo y la espera ya en el clic, y los dos se quedan con la acción hasta
     * que `enviar` se los lleva o `terminar` la abandona. Si ya hay una en vuelo sobre lo mismo, no
     * empieza y lo dice.
     *
     * **Y salir de la pantalla también la abandona**, por el mismo apunte que suelta la espera
     * ({@link alDejarDeMandar}). Con solo la espera, una consulta previa que no contestaba nunca
     * dejaba el cerrojo cogido: la ruta es `keepAlive`, así que al volver no había `continuar` ni
     * cierre del popup que lo soltara y todo Guardar posterior se rechazaba como «acción en vuelo»
     * (revisión CODEX del PR #1842, ronda 2 del 21/10). La que ya ha salido no se toca: `terminar`
     * no suelta lo que es del envío. El cambio de cuenta no hace falta apuntarlo aquí: ése ya tira
     * todos los cerrojos (`unaAccionALaVez`).
     *
     * @return si ha empezado
     */
    empezar: function() {
      if (yaSeEstaResolviendo()) return false;
      propio = marcarEnVuelo(clave);
      preloader.mostrar();
      if (!dejarDeApuntarLaSalida) dejarDeApuntarLaSalida = alDejarDeMandar(vm, terminar);
      return true;
    },
    /**
     * La continuación de una consulta previa, **la del éxito y la del fallo**, sellada como el
     * resto. Suelta primero la espera de la consulta, que es de esta acción y de nadie más. Si la
     * sesión, la pantalla o la ventana ya no son las del clic, la acción se abandona **sin decir
     * nada**: no la ha aceptado nadie, y quien la empezó ya no está delante para leer el aviso.
     * Salió con el rechazo de la consulta del alta, que abría su error sobre la cuenta que hubiera
     * entrado (revisión CODEX del PR #1842, ronda 4). Y lo mismo si el formulario del que salió ya
     * está cerrado (`sigueAbierto`): cerrar el popup con la consulta en vuelo es cancelar, y el
     * alta de una tarea normal se guardaba igual (revisión del 21/10/2026).
     */
    continuar: function(seguir) {
      return function() {
        preloader.cerrar();
        if (!sigueLaSesion(deLaSesion) || !sigueDelante() || !sigueLaVentana() || !sigueAbierto()) {
          terminar();
          return undefined;
        }
        return seguir.apply(null, arguments);
      };
    },
    /**
     * Abandona sin enviar una acción empezada: suelta su cerrojo y su espera, y cierra su pregunta
     * si la tiene abierta. Idempotente.
     */
    terminar: terminar,
    /** Una confirmación: aprobar, anular. */
    confirmar: function(texto, titulo, aceptar) {
      return preguntar(function(sellarBoton, cancelar) {
        return app.dialog.confirm(texto, titulo, sellarBoton(aceptar), cancelar);
      });
    },
    /** Un `prompt`: denegar, que exige motivo. Recibe lo escrito, como el de Framework7. */
    pedir: function(texto, titulo, aceptar) {
      return preguntar(function(sellarBoton, cancelar) {
        return app.dialog.prompt(texto, titulo, sellarBoton(aceptar), cancelar);
      });
    },
    /**
     * **Un diálogo con botones propios** (`dialog.create`): los rótulos en español, o más de dos
     * botones. Recibe los mismos parámetros que `dialog.create`; cada botón con `onClick` es uno que
     * acepta, y los que acepten comparten la misma pregunta —el primero se la queda—. Los que no
     * llevan `onClick` cancelan. El cierre es de aquí: la pantalla no guarda el diálogo ni escucha
     * su `closed` para soltar nada.
     */
    abrir: function(parametros) {
      return preguntar(function(sellarBoton) {
        return app.dialog.create(Object.assign({}, parametros, {
          buttons: (parametros.buttons || []).map(function(boton) {
            return boton.onClick ? Object.assign({}, boton, { onClick: sellarBoton(boton.onClick) }) : boton;
          })
        }));
      }, true);
    },
    /**
     * La acción ya en vuelo: su acuse y su error son de la sesión **y de la pantalla** que la
     * empezó, y mientras viaja la pantalla queda marcada como ocupada.
     *
     * **`siempre` es la excepción, y tiene que serlo.** Es donde el llamante suelta lo que cogió
     * él —su cerrojo, su testigo del reloj—, y eso no es un efecto sobre nadie: es contabilidad
     * suya. Colgado de `exito`/`fallo` se quedaba sin ejecutar justo cuando más falta hace —la
     * respuesta llega con la sesión ya cambiada— y la pantalla se quedaba con el botón apagado
     * «anulando…» y el indicador puesto para siempre. Corre antes que los demás, que es lo que
     * necesita quien recarga en `exito`: soltar primero, pedir después.
     *
     * @param promesa la petición, creada ya dentro del callback sellado
     * @param manejadores `exito` y `fallo`, que son de la ventana, `pantalla` para lo que es de la
     *        pantalla y sobrevive a cerrar la ventana, `fuera` para lo que no es de ninguna de las
     *        dos, `siempre` para lo que el llamante suelta pase lo que pase, y `conEspera: false`
     *        cuando el llamante ya ha cogido su propio testigo
     */
    enviar: function(promesa, manejadores) {
      const al = manejadores || {};
      // El testigo de la espera lo pide esto **salvo que el llamante ya tenga el suyo**. «Mis
      // vacaciones» lo tiene: su registro de cargas coge uno por petición y lo suelta en las tres
      // salidas, así que pedir otro aquí no solo contaría dos veces —serían dos testigos para una
      // petición— sino que el segundo no se soltaría al salir de la pantalla, y el indicador se
      // quedaba encendido sobre la pantalla de destino hasta que contestara el servidor.
      const conEspera = al.conEspera !== false;
      if (conEspera) preloader.mostrar();
      // Desde que sale la petición y hasta que termina, no desde que contesta: el hueco que había
      // entre una cosa y otra es el que dejaba mandar la misma resolución dos veces. El cerrojo es
      // de la solicitud y vive en el módulo, así que desmontar la pantalla no lo mueve.
      // Si la acción se empezó con `empezar`, el cerrojo ya lo tiene y pasa al envío.
      const soltar = propio || marcarEnVuelo(clave);
      propio = null;
      enviada = true;
      noApuntarLaSalida();
      // #663 · Adoptada como promesa común: si la sesión cambia en vuelo, la frontera calla las
      // continuaciones, y aquí van `siempre` y las sueltas de abajo, que no pueden quedarse sin
      // ejecutar. Lo que es de la pantalla ya está detrás de sus sellos.
      return comoPromesaComun(promesa)
        .then(function(res) {
          if (al.siempre) al.siempre();
          if (!sigueLaSesion(deLaSesion)) return;
          // Lo de fuera de la pantalla —el badge del menú lateral— va solo con el sello de sesión:
          // la resolución ocurrió, y que quien la pidió se haya ido a otra pantalla no deja el
          // número del menú con el de antes.
          if (al.fuera) al.fuera(res);
          // De dentro hacia fuera, y cada uno con su pregunta: la recarga es de la pantalla y
          // sobrevive a que la ventana se haya cerrado o se haya reabierto sobre otra persona; el
          // acuse y el cierre son de la ventana y no se le enseñan a la de al lado.
          if (sigueDelante() && al.pantalla) al.pantalla(res);
          if (sigueDelante() && sigueLaVentana() && al.exito) al.exito(res);
        })
        .catch(function(e) {
          if (al.siempre) al.siempre();
          if (!esCancelacion(e) && sigueLaSesion(deLaSesion) && sigueDelante() && sigueLaVentana() && al.fallo) {
            al.fallo(e);
          }
        })
        .then(function() {
          // Se suelta DESPUÉS de los manejadores: la recarga (`pantalla`) coge su propio testigo y
          // así los botones no se encienden en el hueco entre resolver y tener los datos nuevos.
          soltar();
          if (conEspera) preloader.cerrar();
        });
    }
  };
}
